import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { FormattedDate, FormattedTime } from 'react-intl';
import { Icon, Button, InputInline, AvatarGroup, Avatar, Chip } from '@arnsbogroup/react-voicearchive'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import classNames from 'classnames'
import PreviewFileModal from 'components/PreviewFileModal'
import DateTime from "react-datetime"
import useCurrentUser from 'hooks/useCurrentUser'

const Span = ({ children, className }) => <span className={className} >{ children }</span>

const Paragraph = (props) => <p {...props} >{ props.children }</p>

const RowInput = (props) => (
  <div className={props.className}>
    <InputInline
      placeholder={props.placeholder}
      value={props.value}
      onBlur={({ target: { value } }) => props.onChange(value)}
    />
  </div>
)

const priorityIcons = [
  { prioritySlug: 'voicestar', icon: 'star', className: 'text-primary', title: 'Voice Star' },
  { prioritySlug: 'custom_rates', icon: 'funnel-dollar', className: 'text-purple-600', title: 'Custom rates' },
]

const PriorityIcons = ({priorities}) => (
  <>
    {priorityIcons.map(({ icon, prioritySlug, className, title }) => 
      priorities.includes(prioritySlug) && 
        <Icon
          title={title}
          className={`text-xs ${className}`}
          icon={["fas", icon]}
        />
    )}
  </>
)

const Timestamp = ({ children, ...props }) => {
  if (+children == 0) {
    return (
      <span className="text-gray-400">
        <FormattedMessage {...messages.timestampUnknown } />
      </span>
    )
  }

  const value = new Date(parseInt(children) * 1000)

  return (
    <div className={classNames('space-x-1 flex', props.className)}>
      {props.includeStatus &&
        <span className="my-auto">
          <TimestampStatus>{children}</TimestampStatus>
        </span>
      }
      <span>
        <FormattedDate value={value} />
      </span>
      <span>
        <FormattedTime value={value} />
      </span>
    </div>
  )
}

const TimestampStatus = ({ children }) => {
  const { formatMessage } = useIntl()
  let status = {
    background: "",
    title: ""
  }
  if (parseInt(children) - Date.now()/1000 > 86400) {
    status = {
      background: "bg-green-500",
      title: formatMessage(messages.deadlineFar)
    }
  } else if (parseInt(children) - Date.now()/1000 > 0) {
    status = {
      background: "bg-yellow-500",
      title: formatMessage(messages.deadlineClose)
    }
  } else {
    status = {
      background: "bg-red-600",
      title: formatMessage(messages.deadlinePassed)
    }
  }

  return (
      <i
          className={`block rounded-full w-2 h-2 ${status.background}`}
          title={status.title}
      ></i>
  )
}

const FilesReadyForProduction = ({ producerIsNotified, customerapproval }) => {
  if (+producerIsNotified && !(+customerapproval)) {
    return (
      <span className="text-center text-sm">
        <i class="fa fa-file-check text-green-400 fa-fw" title="Files are ready for production"></i>
      </span>
    )
  }
  return null
}

const Contact = ({ contact_email, contact_image, contact_name }) => (
  <div className="flex items-center w-full truncate justify-center xl:justify-start" title={`${contact_name} (${contact_email})`}>
    <div className="shrink-0 h-8 w-8">
      { contact_image ? (
        <img className="h-8 w-8 rounded-full" src={contact_image} alt="" />
      ) : (
        <div className="w-8 h-8 block rounded-full bg-gray-200 flex justify-center items-center">
          <Icon icon="user" />
        </div>
      ) }
    </div>
    <div className="ml-4 hidden xl:block truncate">
      <div className="text-sm font-medium text-gray-900 truncate">
        { contact_name }
      </div>
      <div className="text-sm text-gray-500 truncate">
        { contact_email }
      </div>
    </div>
  </div>
)

const FileVisibility = ({ hidefile }) => (
  <Icon
    className={classNames('text-xs', Boolean(Number(hidefile)) ? 'text-gray-300' : 'text-grayscale-lighter')}
    icon={['fal', Boolean(Number(hidefile)) ? 'eye-slash' : 'eye']}
  />
)

const FileOwnerType = ({ issource }) => (
  <Icon
    className={classNames('text-sm', {
      'text-grayscale-lighter': !!+issource,
      'text-positive': !+issource,
    })}
    icon={['fal', +issource ? 'house' : 'microphone']}
  />
)

const FileDownloadStatus = ({ downloaded }) => (
  <Icon
    className={classNames('text-xs', Boolean(Number(downloaded)) ? 'text-positive' : 'text-gray-300')}
    icon={['fal', Boolean(Number(downloaded)) ? 'circle-check' : 'circle-down']}
  />
)

const FileMeta = (props) => {
  const { onChange, ...file } = props
  const { ext, duration, bitrate, samplerate, wordcount, filesize } = file


  if (ext == 'Audio File') {
    return (
      <p className={classNames('space-x-2', props.className)} >
        <span>{ Math.round(samplerate / 1000).toFixed(1) }kHz</span>
        <span>{ bitrate }</span>
        <span>{ duration }</span>
      </p>
    )
  }

  return (
    <p className={classNames('flex space-x-1', props.className)} >
      <InputInline
        type="number"
        placeholder={wordcount}
        value={!!+wordcount ? wordcount : ''}
        onBlur={({ target: { value } }) => onChange({ ...file, wordcount: value })}
      />
      <span>words</span>
    </p>
  )
}

const FilePreview = ({ uid, ext }) => {
  const { roles } = useCurrentUser()

  const onClick = () => {
    if (roles.includes('coordinator')) {
      const windowFeatures = 'width=600,height=400,top=100,left=100'
      window.open(`/preview/${uid}`, '_blank', windowFeatures)
    } else {
      NiceModal.show(PreviewFileModal, { fileId: uid }) 
        .catch((error) => {
          console.error(error)
        })
    }
  }

  if (ext == 'Document') {
    return (
      <Button variant="inline" onClick={onClick}>Show preview</Button>
    )
  }

  if (ext == 'Audio File') {
    return (
      <audio
        style={{
          width: "100%",
          minWidth: '180px',
          height: '25px',
        }}
        preload="none"
        src={`/stream/task/file/${uid}`}
        controls
        controlsList="nodownload"
      >
        Your browser does not support the audio element. Please download and listen on an external player.
      </audio>
    )
  }

  return (
    <span>No preview available</span>
  )
}

const CompanyId = ({ company_id }) => (company_id > 0 ?
  <a
    onClick={(e) => e.stopPropagation()}
    href={`https://my.voicearchive.com/crm/company/details/${company_id}/`}
    target="_blank"
    className="text-primary hover:text-primary-darker"
  >
    { company_id } <Icon icon="external-link" className="text-xs" />
  </a> : null
)

const TeamAvatars = ({ primary, secondary }) => (
  <AvatarGroup>
    { primary?.id &&
      <Avatar
        src={primary.image}
        title={`#1: ${primary.name}`}
        initials={primary?.name.match(/(\b\S)?/g).join("").match(/(^\S|\S$)?/g).join("").toUpperCase()}
        className="z-20"
      />
    }

    { secondary?.id &&
      <Avatar
        src={secondary.image}
        title={`#2: ${secondary.name}`}
        initials={secondary?.name.match(/(\b\S)?/g).join("").match(/(^\S|\S$)?/g).join("").toUpperCase()}
        className="z-10"
      />
    }
  </AvatarGroup>
)

const Buyouts = ({buyouts, supplierApproval = 0, declinedReason = ''}) => {
  return <div className="space-y-1">
    { buyouts.map(({ quantity, quantity_type, ordertargetmedia, orderusage, territoryMarket, amount, currency }) => {
      if (!ordertargetmedia || !orderusage || !territoryMarket) {
        return null
      }

      const status = getBuyoutStatus(supplierApproval, declinedReason)

      return (
        <div>
          <Chip size="xs" title={status} state={
            {
              accepted:   'success',
              rejected:   'error',
              pending: 'warning'
            }[status] ?? 'warning'
          }>
            {`${quantity} ${quantity > 1 ? quantity_type + 's' : quantity_type} | ${ordertargetmedia} | ${territoryMarket} | ${orderusage} | ${amount == 0 ? '_' : amount} ${currency}`}
          </Chip>
        </div>
      )
    }).filter((entry) => entry)}
  </div>
}

const getBuyoutStatus = (supplierApproval, declinedReason) => {
  let status = 'pending'
  if (!(+supplierApproval) && declinedReason != '') {
    status = 'rejected'
  } else if (!!(+supplierApproval)) {
    status = 'accepted'
  }
  return status
}

const DataTableDateTime = ({ value, onChange, ...props }) => {
  const [date, setDate] = useState(value)
  return (
      <DateTime
        value={date}
        onChange={(value) => setDate(value)}
        onClose={() => onChange(date)}
        inputProps={{ disabled: props.disabled, className: 'text-center rounded-sm w-full border-t-gray-300 border p-2' }}
        {...props}
      />
  )
}

export {
  Span,
  Paragraph,
  RowInput,
  Timestamp,
  TimestampStatus,
  Contact,
  FilesReadyForProduction,
  FileVisibility,
  FileOwnerType,
  FileDownloadStatus,
  FileMeta,
  FilePreview,
  CompanyId,
  TeamAvatars,
  PriorityIcons,
  Buyouts,
  DataTableDateTime
}
